import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert
} from '@mui/material';
// Import DatePicker and LocalizationProvider
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CustomButtonContained from 'src/components/CustomButton/CustomButtonContained';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch } from 'react-redux';
import { useInspectionRecommendations } from './useInspectionRecommendations';
import AIInspectionRecommendations from './AIInspectionRecommendations';

function ScheduleInspectionModal({ open, onClose, applicationID, applicationDetails }) {
  // Store the selected subcode object that contains both name and ID
  const [selectedSubcode, setSelectedSubcode] = useState(null);
  const [selectedInspectionType, setSelectedInspectionType] = useState(null);
  const [availableInspectionTypes, setAvailableInspectionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subcodes, setSubcodes] = useState([]);
  const [fetchingSubcodes, setFetchingSubcodes] = useState(false);
  const [fetchingInspectionTypes, setFetchingInspectionTypes] = useState(false);

  const dispatch = useDispatch()

  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [fetchingTimeSlots, setFetchingTimeSlots] = useState(false);

  const { recommendations, loading: aiLoading, error: aiError } = useInspectionRecommendations(
    selectedSubcode,
    applicationDetails,
    applicationID
  );

  useEffect(() => {
    const fetchSubcodes = async () => {
      if (!open || !applicationID) return;

      setFetchingSubcodes(true);
      try {
        const response = await httpRequest('GET', `/construction/application/${applicationID}/subCodes`);

        if (response.status === 'success' && Array.isArray(response.data)) {
          setSubcodes(response.data);
        } else {
          console.error('Error fetching subcodes:', response);
        }
      } catch (error) {
        console.error('Error fetching subcodes:', error);
      } finally {
        setFetchingSubcodes(false);
      }
    };

    fetchSubcodes();
  }, [applicationID, open]);

  // Fetch inspection types when subcode changes
  useEffect(() => {
    const fetchInspectionTypes = async () => {
      if (!selectedSubcode) return;

      setFetchingInspectionTypes(true);
      setAvailableInspectionTypes([]);
      setSelectedInspectionType(null); // Reset inspection type when subcode changes

      try {
        const subcodeId = selectedSubcode.SubcodeNameID;
        const response = await httpRequest('GET', `/construction/inspectionTypes/getTypes/${subcodeId}`);

        if (response.status === 'success' && response.data && Array.isArray(response.data.inspectionTypes)) {
          setAvailableInspectionTypes(response.data.inspectionTypes);
        } else {
          console.error('Error fetching inspection types:', response);
          dispatch(handleError(response?.message || 'Failed to fetch inspection types'));
        }
      } catch (error) {
        console.error('Error fetching inspection types:', error);
        dispatch(handleError('Failed to fetch inspection types'));
      } finally {
        setFetchingInspectionTypes(false);
      }
    };

    fetchInspectionTypes();
  }, [selectedSubcode, dispatch]);

  // Fetch available time slots when date and subcode are selected
  useEffect(() => {
    const fetchAvailableTimeSlots = async () => {
      if (!selectedDate || !selectedSubcode) return;

      setFetchingTimeSlots(true);
      setAvailableTimeSlots([]);
      setSelectedTimeSlot(null); // Reset selected time slot

      try {
        const formattedDate = format(selectedDate, 'MM/dd/yyyy');
        const subcodeId = selectedSubcode.SubcodeNameID;

        const response = await httpRequest('GET', `/construction/application/inspector/all-availability?InspectionDate=${formattedDate}&SubcodeID=${subcodeId}`);

        if (response.status === 'success' && response.data && Array.isArray(response.data.availableTimeSlots)) {
          setAvailableTimeSlots(response.data.availableTimeSlots);
        } else {
          console.error('Error fetching available time slots:', response);
          dispatch(handleError(response?.message || 'Failed to fetch available time slots'));
        }
      } catch (error) {
        console.error('Error fetching available time slots:', error);
        dispatch(handleError('Failed to fetch available time slots'));
      } finally {
        setFetchingTimeSlots(false);
      }
    };

    fetchAvailableTimeSlots();
  }, [selectedDate, selectedSubcode, dispatch]);

  const handleSubcodeChange = (e) => {
    setSelectedSubcode(e.target.value);
    // Reset dependent fields when subcode changes
    setSelectedDate(null);
    setAvailableTimeSlots([]);
    setSelectedTimeSlot(null);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    // Reset time slot selection when date changes
    setSelectedTimeSlot(null);
  };

  // Function to format time from 24-hour to 12-hour format
  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Function to format a Date object to YYYY-MM-DD string
  const formatDateForApi = (date) => {
    return date ? format(date, 'yyyy-MM-dd') : '';
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formattedDate = formatDateForApi(selectedDate);

      const payload = {
        SubcodeName: selectedSubcode?.SubcodeName,
        InspectionType: selectedInspectionType?.InspectionTypeName,
        InspectionTypeID: selectedInspectionType?.InspectionTypeID,
        SubcodeNameID: selectedSubcode?.SubcodeNameID,
        AvailabilityOptions: [
          {
            Date: formattedDate,
            TimeSlots: [`${formatTime(selectedTimeSlot.startTime)} - ${formatTime(selectedTimeSlot.endTime)}`],
            TimeSlotID: selectedTimeSlot.timeSlotID
          }
        ],
      }

      const response = await httpRequest('POST', `/construction/application/${applicationID}/inspection`, payload)

      if (response.status === 'success') {
        toast.success("Inspection requested!")
      } else {
        dispatch(handleError(response?.message));
      }

      // On successful submission
      onClose();
    } catch (error) {
      console.error('Error scheduling inspection:', error);
      dispatch(handleError('Failed to schedule inspection'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Schedule Inspection
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="subcode-select-label">Select Subcode</InputLabel>
              <Select
                labelId="subcode-select-label"
                id="subcode-select"
                value={selectedSubcode || ''}
                label="Select Subcode"
                onChange={handleSubcodeChange}
                disabled={fetchingSubcodes}
              >
                {subcodes.map((item, index) => (
                  <MenuItem
                    key={index}
                    value={item}
                  >
                    {item.SubcodeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* AI Inspection Recommendations - NEW */}
            {selectedSubcode && (
              <AIInspectionRecommendations
                recommendations={recommendations}
                loading={aiLoading}
                error={aiError}
                selectedSubcode={selectedSubcode}
              />
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="inspection-type-label">Inspection Type</InputLabel>
              <Select
                labelId="inspection-type-label"
                id="inspection-type"
                value={selectedInspectionType || ''}
                label="Inspection Type"
                onChange={(e) => setSelectedInspectionType(e.target.value)}
                disabled={!selectedSubcode || fetchingInspectionTypes || availableInspectionTypes.length === 0}
              >
                {availableInspectionTypes.map((type, index) => (
                  <MenuItem key={index} value={type}>
                    {type.InspectionTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Select Date
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <DatePicker
                    label="Inspection Date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    disabled={!selectedSubcode}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium"
                      }
                    }}
                    sx={{ width: '100%', mb: 2 }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            {/* Available Time Slots */}
            {selectedDate && selectedSubcode && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Available Time Slots
                </Typography>

                {fetchingTimeSlots ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : availableTimeSlots.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No available time slots for the selected date.
                  </Alert>
                ) : (
                  <FormControl component="fieldset" sx={{ mb: 3 }}>
                    <RadioGroup
                      value={selectedTimeSlot?.timeSlotID || ''}
                      onChange={(e) => {
                        const timeSlotId = parseInt(e.target.value);
                        const timeSlot = availableTimeSlots.find(slot => slot.timeSlotID === timeSlotId);
                        setSelectedTimeSlot(timeSlot);
                      }}
                    >
                      {availableTimeSlots.map((slot) => (
                        <FormControlLabel
                          key={slot.timeSlotID}
                          value={slot.timeSlotID}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Available Capacity: {slot.availableCapacity} inspector(s)
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              </>
            )}

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <CustomButtonContained
            title="Cancel"
            onClick={onClose}
            sx={{ mr: 1, backgroundColor: '#9e9e9e' }}
          />
          <CustomButtonContained
            title={loading ? 'Requesting...' : 'Request Schedule'}
            onClick={handleSubmit}
            disabled={
              !selectedSubcode ||
              !selectedInspectionType ||
              !selectedDate ||
              !selectedTimeSlot ||
              // !timezone ||
              loading
            }
            startIcon={loading ? <CircularProgress size={20} /> : null}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ScheduleInspectionModal;